// ==============================
// ChartsTabsView.tsx
// ==============================
import React, { useState } from "react";
import { Box, Typography, Tabs, Tab } from "@mui/material";
import { Line, Bar, Pie, Radar } from "react-chartjs-2";

import { User, Workout, Meal, WaterEntry } from "../../../constants/types";
import ChartCard from "./chart-card";
import { intervals, isWithinDays } from "./helpers";

interface ChartsTabsViewProps {
  users: User[];
  workouts: Workout[];
}

export default function ChartsTabsView({
  users,
  workouts,
}: ChartsTabsViewProps) {
  // We use an index to control which tab is active
  const [tabIndex, setTabIndex] = useState(0);

  // ==============================
  // EXISTING DATA CALCULATIONS
  // ==============================

  // 1) Number of Workouts Over Time (Line)
  const sortedByDate = [...workouts].sort(
    (a, b) =>
      new Date(a.dayOfExecution).getTime() -
      new Date(b.dayOfExecution).getTime()
  );
  const workoutsPerDate: Record<string, number> = {};
  sortedByDate.forEach((w) => {
    const dateStr = new Date(w.dayOfExecution).toLocaleDateString();
    workoutsPerDate[dateStr] = (workoutsPerDate[dateStr] || 0) + 1;
  });
  const chart1Data = {
    labels: Object.keys(workoutsPerDate),
    datasets: [
      {
        label: "Number of Workouts",
        data: Object.values(workoutsPerDate),
        borderColor: "rgba(75,192,192,1)",
        fill: false,
      },
    ],
  };

  // 2) Total Volume Over Time (Line)
  const volumePerDate: Record<string, number> = {};
  sortedByDate.forEach((w) => {
    const dateStr = new Date(w.dayOfExecution).toLocaleDateString();
    volumePerDate[dateStr] = (volumePerDate[dateStr] || 0) + w.totalVolume;
  });
  const chart2Data = {
    labels: Object.keys(volumePerDate),
    datasets: [
      {
        label: "Total Volume",
        data: Object.values(volumePerDate),
        borderColor: "rgba(153,102,255,1)",
        fill: false,
      },
    ],
  };

  // 3) Distribution of Training Goals (Pie)
  const trainingGoalCount: Record<string, number> = {};
  users.forEach((u) => {
    const goal = u.goals?.trainingGoal || "unknown";
    trainingGoalCount[goal] = (trainingGoalCount[goal] || 0) + 1;
  });
  const chart3Data = {
    labels: Object.keys(trainingGoalCount),
    datasets: [
      {
        data: Object.values(trainingGoalCount),
        backgroundColor: [
          "#ff6384",
          "#36a2eb",
          "#ffce56",
          "#4bc0c0",
          "#9966ff",
        ],
      },
    ],
  };

  // 4) Distribution of Activity Levels (Pie)
  const activityCount: Record<string, number> = {};
  users.forEach((u) => {
    const level = u.activityLevel || "unknown";
    activityCount[level] = (activityCount[level] || 0) + 1;
  });
  const chart4Data = {
    labels: Object.keys(activityCount),
    datasets: [
      {
        data: Object.values(activityCount),
        backgroundColor: [
          "#ff6384",
          "#36a2eb",
          "#ffce56",
          "#4bc0c0",
          "#9966ff",
        ],
      },
    ],
  };

  // 5) Gender Distribution (Pie)
  const genderCount: Record<string, number> = {};
  users.forEach((u) => {
    genderCount[u.gender] = (genderCount[u.gender] || 0) + 1;
  });
  const chart5Data = {
    labels: Object.keys(genderCount),
    datasets: [
      {
        data: Object.values(genderCount),
        backgroundColor: ["#ff9f40", "#9966ff"],
      },
    ],
  };

  // 6) Average Volume per Workout per User (Bar)
  const volumeMap: Record<string, { total: number; count: number }> = {};
  workouts.forEach((w) => {
    if (!w.userId) return;
    volumeMap[w.userId] = volumeMap[w.userId] || { total: 0, count: 0 };
    volumeMap[w.userId].total += w.totalVolume;
    volumeMap[w.userId].count += 1;
  });
  const userLabels = users.map((u) => u.name);
  const chart6Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Avg Volume",
        data: users.map((u) => {
          const stats = volumeMap[u.userId];
          return stats ? stats.total / stats.count : 0;
        }),
        backgroundColor: "#36a2eb",
      },
    ],
  };

  // 7) Total Water Intake by User (Bar)
  const chart7Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Total Water (ml/oz)",
        data: users.map(
          (u) =>
            u.waterEntries?.reduce(
              (acc: number, we: WaterEntry) => acc + we.amount,
              0
            ) || 0
        ),
        backgroundColor: "#4bc0c0",
      },
    ],
  };

  // 8) Number of Meals Logged by User (Bar)
  const chart8Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Meals Logged",
        data: users.map((u) => (u.meals ? u.meals.length : 0)),
        backgroundColor: "#ffce56",
      },
    ],
  };

  // 9) Average Daily Calories vs. Calorie Goal (Bar)
  const now = new Date().getTime();
  const averageCaloriesData: number[] = [];
  const calorieGoalData: number[] = [];

  users.forEach((u) => {
    const totalCals =
      u.meals?.reduce((acc: number, m: Meal) => acc + m.calories, 0) || 0;
    let days = 1;
    if (u.meals && u.meals.length > 0) {
      const firstMealDate = new Date(u.meals[0].dateTime).getTime();
      const diff = (now - firstMealDate) / (1000 * 3600 * 24);
      days = diff < 1 ? 1 : Math.round(diff);
    }
    const avgCals = Math.round(totalCals / days);
    averageCaloriesData.push(avgCals);

    const goal = u.goals?.caloriesGoal || 2000;
    calorieGoalData.push(goal);
  });
  const chart9Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Avg Daily Calories",
        data: averageCaloriesData,
        backgroundColor: "#2ac1f2",
      },
      {
        label: "Calorie Goal",
        data: calorieGoalData,
        backgroundColor: "#f2562a",
      },
    ],
  };

  // A) Users Updated in last Day/Week/Month/Year
  const userUpdates: number[] = intervals.map(({ days }) => {
    return users.filter((u: User) => {
      const lastUpdate = u.updated ? new Date(u.updated) : null;
      return lastUpdate ? isWithinDays(lastUpdate, days) : false;
    }).length;
  });
  const userUpdatesChartData = {
    labels: intervals.map((i) => i.label),
    datasets: [
      {
        label: "Users Updated",
        data: userUpdates,
        backgroundColor: "#8884d8",
      },
    ],
  };

  // B) Count how many workouts occurred in day/week/month/year
  const workoutsInInterval: number[] = intervals.map(({ days }) => {
    return workouts.filter((w) =>
      isWithinDays(new Date(w.dayOfExecution), days)
    ).length;
  });
  const workoutsTimeChartData = {
    labels: intervals.map((i) => i.label),
    datasets: [
      {
        label: "Workouts",
        data: workoutsInInterval,
        backgroundColor: "#82ca9d",
      },
    ],
  };

  // KPIs
  const allWaterEntries = users.flatMap((u) => u.waterEntries || []);
  const allMeals = users.flatMap((u) => u.meals || []);

  const userSignupsKPI = intervals.map(({ days }) => {
    return users.filter((u) => {
      if (!u.created) return false;
      return isWithinDays(new Date(u.created), days);
    }).length;
  });

  const waterEntriesKPI = intervals.map(({ days }) => {
    return allWaterEntries.filter((we) =>
      isWithinDays(new Date(we.dateTime), days)
    ).length;
  });

  const mealsKPI = intervals.map(({ days }) => {
    return allMeals.filter((m) => isWithinDays(new Date(m.dateTime), days))
      .length;
  });

  // ==============================
  // NEW: ADDITIONAL INSIGHTFUL CHARTS (10 More)
  // GROUP 1: Body Composition & Age
  // ==============================

  // 10) BMI Distribution (Bar)
  // We categorize into 4 bins: Underweight (<18.5), Normal (18.5-24.9), Overweight (25-29.9), Obese (30+)
  const bmiBins = [0, 0, 0, 0]; // underweight, normal, overweight, obese

  function computeBMI(user: User) {
    // If isMetricUnits = true, assume user.weight is in kg, else lbs
    // If user.weight is an array, just use the last entry for simplicity
    let weightValue = Array.isArray(user.weight)
      ? user.weight[user.weight.length - 1]
      : user.weight;
    if (!weightValue || !user.height) return 0;

    // Convert to kg if not metric
    if (!user.isMetricUnits) {
      weightValue = weightValue / 2.205; // Rough lbs -> kg
    }
    const heightInMeters = user.height / 100;
    const bmi = weightValue / (heightInMeters * heightInMeters);
    return bmi;
  }

  users.forEach((u) => {
    const bmi = computeBMI(u);
    if (bmi < 18.5) {
      bmiBins[0]++;
    } else if (bmi < 25) {
      bmiBins[1]++;
    } else if (bmi < 30) {
      bmiBins[2]++;
    } else {
      bmiBins[3]++;
    }
  });

  const chart10Data = {
    labels: ["Underweight", "Normal", "Overweight", "Obese"],
    datasets: [
      {
        label: "Users",
        data: bmiBins,
        backgroundColor: ["#f2c1c1", "#c1f2c1", "#f2e2c1", "#f2c1e2"],
      },
    ],
  };

  // 11) Age Distribution (Bar)
  // We'll bin ages in: <20, 20-29, 30-39, 40-49, 50+
  function getAge(birthday: Date): number {
    if (!birthday) return 0;
    const diff = Date.now() - birthday.getTime();
    return Math.floor(diff / (1000 * 3600 * 24 * 365.25));
  }
  const ageBins = [0, 0, 0, 0, 0];
  users.forEach((u) => {
    const age = getAge(new Date(u.birthday));
    if (age < 20) {
      ageBins[0]++;
    } else if (age < 30) {
      ageBins[1]++;
    } else if (age < 40) {
      ageBins[2]++;
    } else if (age < 50) {
      ageBins[3]++;
    } else {
      ageBins[4]++;
    }
  });
  const chart11Data = {
    labels: ["<20", "20-29", "30-39", "40-49", "50+"],
    datasets: [
      {
        label: "Users",
        data: ageBins,
        backgroundColor: "#a2eb36",
      },
    ],
  };

  // 12) Average BMI by Gender (Bar)
  let totalMaleBmi = 0;
  let maleCount = 0;
  let totalFemaleBmi = 0;
  let femaleCount = 0;

  users.forEach((u) => {
    const bmi = computeBMI(u);
    if (u.gender === "male") {
      totalMaleBmi += bmi;
      maleCount++;
    } else if (u.gender === "female") {
      totalFemaleBmi += bmi;
      femaleCount++;
    }
  });

  const avgMaleBmi = maleCount ? totalMaleBmi / maleCount : 0;
  const avgFemaleBmi = femaleCount ? totalFemaleBmi / femaleCount : 0;

  const chart12Data = {
    labels: ["Male", "Female"],
    datasets: [
      {
        label: "Avg BMI",
        data: [avgMaleBmi.toFixed(1), avgFemaleBmi.toFixed(1)],
        backgroundColor: ["#0080ff", "#ff80c0"],
      },
    ],
  };

  // ==============================
  // GROUP 2: Workout Patterns & Trends
  // ==============================

  // 13) Average Workout Duration by User (Bar)
  const durationMap: Record<string, { totalMinutes: number; count: number }> =
    {};
  workouts.forEach((w) => {
    if (!w.userId) return;
    if (!durationMap[w.userId]) {
      durationMap[w.userId] = { totalMinutes: 0, count: 0 };
    }
    durationMap[w.userId].totalMinutes += w.durationInMinutes;
    durationMap[w.userId].count += 1;
  });
  const chart13Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Avg Workout Duration (min)",
        data: users.map((u) => {
          const stats = durationMap[u.userId];
          return stats ? stats.totalMinutes / stats.count : 0;
        }),
        backgroundColor: "#f2922a",
      },
    ],
  };

  // 14) Distribution of Workouts by Primary Muscle Group
  // We'll attempt to parse the muscleGroup from each workout's exercises
  // Then create a global count for each muscle group
  const muscleGroupCount: Record<string, number> = {};
  workouts.forEach((w) => {
    w.template.exercises.forEach((ex) => {
      const mg = ex.exercise.muscleGroup || "Other";
      muscleGroupCount[mg] = (muscleGroupCount[mg] || 0) + 1;
    });
  });
  const chart14Data = {
    labels: Object.keys(muscleGroupCount),
    datasets: [
      {
        label: "Workouts by Muscle Group",
        data: Object.values(muscleGroupCount),
        backgroundColor: [
          "#f28a2a",
          "#2af292",
          "#92a2f2",
          "#f2b8a2",
          "#d8f22a",
          "#ec2af2",
        ],
      },
    ],
  };

  // 15) Longest Workout Streak by User (Bar)
  // We'll define "streak" as consecutive days with workouts
  function getLongestStreak(user: User): number {
    if (!user.workouts || user.workouts.length === 0) return 0;
    const sorted = [...user.workouts].sort(
      (a, b) =>
        new Date(a.dayOfExecution).getTime() -
        new Date(b.dayOfExecution).getTime()
    );
    let longest = 1;
    let current = 1;

    for (let i = 1; i < sorted.length; i++) {
      const prevDate = new Date(sorted[i - 1].dayOfExecution);
      const currDate = new Date(sorted[i].dayOfExecution);
      const diff =
        (currDate.getTime() - prevDate.getTime()) / (1000 * 3600 * 24);

      if (diff <= 1.5) {
        // treat up to ~1.5 days as consecutive
        current++;
      } else {
        longest = Math.max(longest, current);
        current = 1;
      }
    }
    return Math.max(longest, current);
  }

  const longestStreaks = users.map((u) => getLongestStreak(u));
  const chart15Data = {
    labels: userLabels,
    datasets: [
      {
        label: "Longest Streak (days)",
        data: longestStreaks,
        backgroundColor: "#f22a2a",
      },
    ],
  };

  // ==============================
  // GROUP 3: Nutrition & Engagement
  // ==============================

  // 16) Average Macro Ratio (Protein/Carbs/Fats) by User (Radar)
  // We'll compute the total macros for each user, divide by meal count, and create a radar chart
  const macroRadarLabels = ["Protein", "Carbs", "Fats"];
  const macroDatasets: any[] = [];

  // Each user becomes a dataset line on the radar
  users.forEach((u) => {
    const totalProtein = u.meals?.reduce((acc, m) => acc + m.protein, 0) || 0;
    const totalCarbs = u.meals?.reduce((acc, m) => acc + m.carbs, 0) || 0;
    const totalFats = u.meals?.reduce((acc, m) => acc + m.fats, 0) || 0;
    const mealCount = u.meals?.length || 1; // avoid division by zero
    const avgProtein = totalProtein / mealCount;
    const avgCarbs = totalCarbs / mealCount;
    const avgFats = totalFats / mealCount;

    macroDatasets.push({
      label: u.name,
      data: [avgProtein, avgCarbs, avgFats],
    });
  });

  const chart16Data = {
    labels: macroRadarLabels,
    datasets: macroDatasets.map((ds, i) => ({
      ...ds,
      backgroundColor: "rgba(0,0,0,0)",
      borderColor: `hsl(${(i * 40) % 360}, 70%, 50%)`,
    })),
  };

  // 17) Over vs. Under Calorie Goal (Pie) aggregated across all users
  // We'll count total days above or below the daily calorie goal
  let overCount = 0;
  let underCount = 0;

  users.forEach((u) => {
    if (!u.meals || u.meals.length === 0) return;
    // Group meals by date so we can see total cals per day
    const dailyTotals: Record<string, number> = {};
    u.meals.forEach((m) => {
      const dateStr = new Date(m.dateTime).toDateString();
      dailyTotals[dateStr] = (dailyTotals[dateStr] || 0) + m.calories;
    });
    const userGoal = u.goals?.caloriesGoal || 2000;

    Object.keys(dailyTotals).forEach((day) => {
      if (dailyTotals[day] > userGoal) {
        overCount++;
      } else {
        underCount++;
      }
    });
  });

  const chart17Data = {
    labels: ["Over Goal", "Under Goal"],
    datasets: [
      {
        data: [overCount, underCount],
        backgroundColor: ["#f25e2a", "#2af25e"],
      },
    ],
  };

  // 18) Time-of-Day Distribution for Meal Logging (Bar)
  // We'll count how many meals fall within each hour of the day
  const hourCounts = new Array(24).fill(0);
  allMeals.forEach((m) => {
    const mealDate = new Date(m.dateTime);
    const hour = mealDate.getHours();
    hourCounts[hour]++;
  });

  const chart18Data = {
    labels: hourCounts.map((_, i) => i.toString().padStart(2, "0") + ":00"),
    datasets: [
      {
        label: "Meals",
        data: hourCounts,
        backgroundColor: "#2a9af2",
      },
    ],
  };

  // 19) Pro vs. Free User Distribution (Pie)
  let proCount = 0;
  let freeCount = 0;
  users.forEach((u) => {
    if (u.isProUser) {
      proCount++;
    } else {
      freeCount++;
    }
  });
  const chart19Data = {
    labels: ["Pro Users", "Free Users"],
    datasets: [
      {
        data: [proCount, freeCount],
        backgroundColor: ["#f2a12a", "#ccc"],
      },
    ],
  };

  // ==============================
  // GROUP-SPECIFIC SUMMARIES
  // ==============================

  function getBodyCompositionSummary() {
    // Example dynamic summary for Group 1 (BMI & Age)
    const totalUsers = users.length || 1;
    const underPct = ((bmiBins[0] / totalUsers) * 100).toFixed(1);
    const normalPct = ((bmiBins[1] / totalUsers) * 100).toFixed(1);
    const avgAge =
      users.reduce((acc, u) => acc + getAge(new Date(u.birthday)), 0) /
      totalUsers;
    return (
      `Among all users, ${underPct}% are considered underweight, ` +
      `${normalPct}% are in a normal BMI range, and the average age of our population ` +
      `hovers around ${avgAge.toFixed(
        1
      )} years. Interestingly, there appears to be a slight difference in BMI ` +
      `when comparing males to females, which may suggest varied training or dietary patterns between genders.`
    );
  }

  function getWorkoutPatternsSummary() {
    // Example dynamic summary for Group 2 (Workout Patterns)
    const avgDuration =
      workouts.reduce((acc, w) => acc + w.durationInMinutes, 0) /
      (workouts.length || 1);
    const topMuscleGroup = Object.entries(muscleGroupCount).reduce(
      (best, [k, v]) => (v > best[1] ? [k, v] : best),
      ["N/A", 0]
    );
    const maxStreak = Math.max(...longestStreaks);
    return (
      `The average workout duration across all users is around ${avgDuration.toFixed(
        1
      )} minutes. ` +
      `Our most frequently targeted muscle group appears to be ${topMuscleGroup[0]}, ` +
      `demonstrating a focus among many users. Notably, the longest workout streak recorded so far is ` +
      `${maxStreak} consecutive days, which can be a motivator for those seeking consistency.`
    );
  }

  function getNutritionEngagementSummary() {
    // Example dynamic summary for Group 3 (Nutrition & Engagement)
    const totalDays = overCount + underCount || 1;
    const overPct = ((overCount / totalDays) * 100).toFixed(1);
    const freePct = ((freeCount / (freeCount + proCount || 1)) * 100).toFixed(
      1
    );
    return (
      `When examining daily calorie totals, about ${overPct}% of tracked days exceed users' ` +
      `calorie goals, suggesting possible areas for dietary adjustment. It's also interesting to note ` +
      `that roughly ${freePct}% of our user base is on the free tier, while others have chosen to upgrade ` +
      `to Pro for additional features. Meanwhile, meal entries cluster around certain times of day, ` +
      `providing valuable insight into common eating patterns.`
    );
  }

  // ==============================
  // Tabs Content
  // ==============================

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  return (
    <Box
      sx={{
        width: "80vw",
        alignSelf: "flex-start",
        marginLeft: "-20vw",
        height: "100%",
      }}
    >
      <Tabs value={tabIndex} onChange={handleTabChange} centered>
        <Tab label="Over Time" />
        <Tab label="User Distribution" />
        <Tab label="Volume & Water" />
        <Tab label="Meals & Calories" />
        <Tab label="Time-Based" />
        <Tab label="KPIs" />
        <Tab label="Additional Insights" />
      </Tabs>

      {/* Tab Panel 1: Over Time */}
      {tabIndex === 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <ChartCard title="Number of Workouts Over Time">
            <Line data={chart1Data} />
          </ChartCard>
          <ChartCard title="Total Volume Over Time">
            <Line data={chart2Data} />
          </ChartCard>
        </Box>
      )}

      {/* Tab Panel 2: User Distribution */}
      {tabIndex === 1 && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <ChartCard title="Training Goals">
            <Pie data={chart3Data} />
          </ChartCard>
          <ChartCard title="Activity Levels">
            <Pie data={chart4Data} />
          </ChartCard>
          <ChartCard title="Gender Distribution">
            <Pie data={chart5Data} />
          </ChartCard>
        </Box>
      )}

      {/* Tab Panel 3: Volume & Water */}
      {tabIndex === 2 && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <ChartCard title="Avg Volume per Workout by User">
            <Bar data={chart6Data} />
          </ChartCard>
          <ChartCard title="Total Water Intake by User">
            <Bar data={chart7Data} />
          </ChartCard>
        </Box>
      )}

      {/* Tab Panel 4: Meals & Calories */}
      {tabIndex === 3 && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <ChartCard title="Meals Logged by User">
            <Bar data={chart8Data} />
          </ChartCard>
          <ChartCard title="Avg Daily Cals vs. Calorie Goal">
            <Bar data={chart9Data} />
          </ChartCard>
        </Box>
      )}

      {/* Tab Panel 5: Time-Based */}
      {tabIndex === 4 && (
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <ChartCard title="Users Updated (Day/Week/Month/Year)">
            <Bar data={userUpdatesChartData} />
          </ChartCard>
          <ChartCard title="Workouts in Last Day/Week/Month/Year">
            <Bar data={workoutsTimeChartData} />
          </ChartCard>
        </Box>
      )}

      {/* Tab Panel 6: KPIs */}
      {tabIndex === 5 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="h6" align="center" gutterBottom>
              KPI: New User Signups
            </Typography>
            {intervals.map(({ label }, idx) => (
              <Typography key={label} variant="body1">
                Last {label}: {userSignupsKPI[idx]}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="h6" align="center" gutterBottom>
              KPI: Water Entries
            </Typography>
            {intervals.map(({ label }, idx) => (
              <Typography key={label} variant="body1">
                Last {label}: {waterEntriesKPI[idx]}
              </Typography>
            ))}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              border: "1px solid #ccc",
              borderRadius: 2,
              p: 2,
            }}
          >
            <Typography variant="h6" align="center" gutterBottom>
              KPI: Meals Logged
            </Typography>
            {intervals.map(({ label }, idx) => (
              <Typography key={label} variant="body1">
                Last {label}: {mealsKPI[idx]}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {/* ==============================
          Tab Panel 7: Additional Insights (10 new charts)
          ============================== */}
      {tabIndex === 6 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {/* GROUP 1: Body Composition & Age */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5">Body Composition & Age</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <ChartCard title="BMI Distribution">
                <Bar data={chart10Data} />
              </ChartCard>
              <ChartCard title="Age Distribution">
                <Bar data={chart11Data} />
              </ChartCard>
              <ChartCard title="Average BMI by Gender">
                <Bar data={chart12Data} />
              </ChartCard>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {getBodyCompositionSummary()}
            </Typography>
          </Box>

          {/* GROUP 2: Workout Patterns & Trends */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5">Workout Patterns & Trends</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <ChartCard title="Avg Workout Duration by User">
                <Bar data={chart13Data} />
              </ChartCard>
              <ChartCard title="Workouts by Muscle Group">
                <Pie data={chart14Data} />
              </ChartCard>
              <ChartCard title="Longest Workout Streak by User">
                <Bar data={chart15Data} />
              </ChartCard>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {getWorkoutPatternsSummary()}
            </Typography>
          </Box>

          {/* GROUP 3: Nutrition & Engagement */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h5">Nutrition & Engagement</Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap" }}>
              <ChartCard title="Avg Macro Ratio by User (Radar)">
                <Radar data={chart16Data} />
              </ChartCard>
              <ChartCard title="Days Over vs. Under Calorie Goal">
                <Pie data={chart17Data} />
              </ChartCard>
              <ChartCard title="Time-of-Day Meal Logging">
                <Bar data={chart18Data} />
              </ChartCard>
              <ChartCard title="Pro vs. Free Users">
                <Pie data={chart19Data} />
              </ChartCard>
            </Box>
            <Typography variant="body1" sx={{ mt: 2 }}>
              {getNutritionEngagementSummary()}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
